import React, { useEffect, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import classNames from 'classnames';
import RenderIf from 'components/RenderIf/RenderIf';
import { ProFormaModelIds } from 'constants/proForma';
import { formatCashFlowPeriods } from 'views/ProFormaTable/helpers';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import ApartmentIncome from './ApartmentIncome/ApartmentIncome';
import RetailOfficeIndustrialIncome from './RetailOfficeIndustrialIncome/RetailOfficeIndustrialIncome';
import DataCenterIncome from './DataCenterIncome/DataCenterIncome';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import { useCustomScroll } from '../../../../hooks/useCustomScroll';
import './ProFormaIncome.scss';

export interface IProFormaIncomeProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const ProFormaIncome = ({ operationBeginIndex, baseCashFlowColumns }: IProFormaIncomeProps) => {
    const { proForma, cashFlowPeriodsByCadence, operatingInfo } = useProFormaSocketContext();

    const {
        tableRef, handleTableScroll, createRefHandler,
    } = useCustomScroll('income-table', ['expenses-table', 'permanent-debt-table']);

    useEffect(() => {
        if (!operationBeginIndex) return undefined;

        requestAnimationFrame(() => {
            if (operationBeginIndex > 0) {
                const scrollAmount = operationBeginIndex * 120;
                tableRef.current?.scrollTo({ left: scrollAmount, behavior: 'smooth' });
            }
        });
        return undefined;
    }, [operationBeginIndex]);

    const monthlyPeriodOptions = useMemo(() => {
        const monthlyPeriodOptions = formatCashFlowPeriods(cashFlowPeriodsByCadence, Periods.MONTHLY);
        if (!operatingInfo?.operationBeginMonth || !operatingInfo?.saleMonth || !monthlyPeriodOptions.length) {
            return [];
        }
        // We do not include the sale month in the options
        return monthlyPeriodOptions.slice(operatingInfo.operationBeginMonth, operatingInfo.saleMonth);
    }, [cashFlowPeriodsByCadence, operatingInfo?.operationBeginMonth, operatingInfo?.saleMonth]);

    return (
        <div
            className={classNames(['ProFormaIncome py-3 pl-3', {
                'pr-3': !operationBeginIndex,
            }])}
        >
            <div
                className="scrollable-container"
                ref={createRefHandler(tableRef)}
                onScroll={() => handleTableScroll('table')}
            >
                <RenderIf isTrue={[ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proForma.proFormaModelId)}>
                    <ApartmentIncome
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                </RenderIf>
                <RenderIf isTrue={proForma.proFormaModelId === ProFormaModelIds.OFFICE
                    || proForma.proFormaModelId === ProFormaModelIds.INDUSTRIAL
                    || proForma.proFormaModelId === ProFormaModelIds.RETAIL}
                >
                    <RetailOfficeIndustrialIncome
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                        monthlyPeriodOptions={monthlyPeriodOptions}
                    />
                </RenderIf>
                <RenderIf isTrue={proForma.proFormaModelId === ProFormaModelIds.DATA_CENTER}>
                    <DataCenterIncome
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                        monthlyPeriodOptions={monthlyPeriodOptions}
                    />
                </RenderIf>
            </div>
        </div>
    );
};

export default ProFormaIncome;
